import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Form, Row, Select, Space, Table, Tooltip, Typography } from 'antd';
import { DownloadOutlined, FormOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { saveAs } from 'file-saver';
import { useNavigate, useParams } from 'react-router-dom';
import './Company.scss';
import CompanyService, { CompanyControlReportGroup } from '../../services/CompanyService';
import ProposalService from '../../services/ProposalService';
import {
  downloadControlTaskReport,
  publishBulkControlTaskReport,
} from '../../modules/inspectors/services/controlTaskService';
import { PageableResponse } from '../../types/pagination';
import { formatDate, normalizeTurkish, openNotification } from '../../utils';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type AttributeOption = {
  id: number;
  name: string;
};

type ProposalOption = {
  id: number;
  proposalNumber: string;
  createdAt?: string | null;
  statusId?: number | null;
  attributes: AttributeOption[];
};

type FilterValues = {
  statusIds?: number[];
  proposalIds?: number[];
  attributeId?: number;
  dateRange?: [Dayjs, Dayjs];
};

const PROPOSAL_STATUS_OPTIONS = [
  { value: 5, label: 'Kontrol ve Raporlama' },
  { value: 4, label: 'Kontrol Tamamlandı' },
];

const extractProposalAttributes = (proposal: any): AttributeOption[] => {
  const result = new Map<number, AttributeOption>();
  const attributes = Array.isArray(proposal?.attributes) ? proposal.attributes : [];

  attributes.forEach((attribute: any) => {
    const attributeId = Number(attribute?.attributeId ?? attribute?.id);
    if (!Number.isFinite(attributeId) || attributeId <= 0) {
      return;
    }
    const name = String(attribute?.attributeName ?? attribute?.name ?? '').trim() || `Parametre #${attributeId}`;
    result.set(attributeId, { id: attributeId, name });
  });

  return Array.from(result.values()).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
};

const extractProposalOptions = (payload: any): ProposalOption[] => {
  const resolved = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  const result: ProposalOption[] = [];

  resolved.forEach((item: any) => {
    const id = Number(item?.id);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    result.push({
      id,
      proposalNumber: String(item?.proposalNumber ?? `Teklif #${id}`).trim() || `Teklif #${id}`,
      createdAt: item?.createdAt ?? null,
      statusId: Number.isFinite(Number(item?.statusId)) ? Number(item.statusId) : null,
      attributes: extractProposalAttributes(item),
    });
  });

  return result.sort((a, b) => b.proposalNumber.localeCompare(a.proposalNumber, 'tr'));
};

const formatProposalDate = (value?: string | null) => (value ? dayjs(value).format('DD/MM/YYYY') : '-');

const buildParameterTitle = (record: CompanyControlReportGroup) => {
  const attributeName = record.attributeName || (record.attributeId ? `Parametre #${record.attributeId}` : '-');
  return record.controlAreaName ? `${attributeName} (${record.controlAreaName})` : attributeName;
};

const buildReportLabel = (record: CompanyControlReportGroup) => (
  record.reportNumber
  || (record.reportSequenceNo ? `${record.reportSequenceNo}. Rapor` : null)
  || (record.responseId ? `Yanıt #${record.responseId}` : null)
  || 'Rapor'
);

const sanitizeFilenamePart = (value: string) => value
  .replace(/[\\/:*?"<>|]+/g, '-')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '')
  .toLocaleLowerCase('tr-TR');

export const buildControlReportFilterQueryParams = (filters: FilterValues) => {
  const params: Record<string, any> = {};
  if (filters.proposalIds?.length) {
    params['proposalId.in'] = filters.proposalIds.join(',');
  }
  if (filters.statusIds?.length) {
    params['proposalStatusId.in'] = filters.statusIds.join(',');
  }
  if (filters.attributeId) {
    params['attributeId.equals'] = filters.attributeId;
  }
  if (filters.dateRange?.[0]) {
    params['date.greaterThanOrEqual'] = filters.dateRange[0].startOf('day').format('YYYY-MM-DDTHH:mm:ss');
  }
  if (filters.dateRange?.[1]) {
    params['date.lessThanOrEqual'] = filters.dateRange[1].endOf('day').format('YYYY-MM-DDTHH:mm:ss');
  }
  return params;
};

const renderDistinctiveAnswers = (record: CompanyControlReportGroup) => {
  const items = Array.isArray(record.distinctiveAnswerItems) ? record.distinctiveAnswerItems : [];
  if (items.length > 0) {
    return (
      <Space direction='vertical' size={0}>
        {items.map((item, index) => (
          <Text type='secondary' key={`${item.questionId ?? index}-${item.questionTitle ?? ''}`}>
            <Text strong type='secondary'>{item.questionTitle || '-'}</Text>
            {`: ${item.answerText || '-'}`}
          </Text>
        ))}
      </Space>
    );
  }
  return record.distinctiveAnswers ? <Text type='secondary'>{record.distinctiveAnswers}</Text> : null;
};

const CompanyControlReports: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm<FilterValues>();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<CompanyControlReportGroup[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState<FilterValues>({});
  const [proposalOptions, setProposalOptions] = useState<ProposalOption[]>([]);
  const [downloadKey, setDownloadKey] = useState<string | null>(null);

  const queryParams = useMemo(() => ({
    ...buildControlReportFilterQueryParams(filters),
    page: pagination.current - 1,
    size: pagination.pageSize,
  }), [filters, pagination.current, pagination.pageSize]);

  const proposalSelectOptions = useMemo(() => proposalOptions.map((proposal) => ({
    value: proposal.id,
    label: proposal.proposalNumber,
    searchLabel: `${proposal.proposalNumber} ${formatProposalDate(proposal.createdAt)}`,
  })), [proposalOptions]);

  const selectedProposalIds = Form.useWatch('proposalIds', form);
  const attributeOptions = useMemo(() => {
    const selectedIds = Array.isArray(selectedProposalIds) ? selectedProposalIds.map(Number).filter(Boolean) : [];
    const sourceProposals = selectedIds.length
      ? proposalOptions.filter((proposal) => selectedIds.includes(proposal.id))
      : proposalOptions;
    const attributesById = new Map<number, AttributeOption>();
    sourceProposals.forEach((proposal) => {
      proposal.attributes.forEach((attribute) => attributesById.set(attribute.id, attribute));
    });
    return Array.from(attributesById.values()).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [proposalOptions, selectedProposalIds]);

  useEffect(() => {
    if (!id) {
      return;
    }
    ProposalService.getAllWithoutPagination({ companyId: id })
      .then((response) => {
        setProposalOptions(extractProposalOptions(response?.data));
      })
      .catch((error) => {
        console.error('Teklif seçenekleri yüklenemedi:', error);
        setProposalOptions([]);
      });
  }, [id]);

  useEffect(() => {
    const selectedAttributeId = form.getFieldValue('attributeId');
    if (!selectedAttributeId) {
      return;
    }
    const stillAvailable = attributeOptions.some((attribute) => attribute.id === Number(selectedAttributeId));
    if (!stillAvailable) {
      form.setFieldsValue({ attributeId: undefined });
    }
  }, [attributeOptions, form]);

  const fetchRows = useCallback(async () => {
    if (!id) {
      setRows([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
      return;
    }
    setLoading(true);
    try {
      const response = await CompanyService.getControlReportGroups(id, queryParams);
      const data = response.data as PageableResponse<CompanyControlReportGroup>;
      setRows(Array.isArray(data?.content) ? data.content : []);
      setPagination((prev) => ({
        ...prev,
        total: Number(data?.totalElements ?? 0),
      }));
    } catch (error) {
      console.error('Firma kontrol raporları yüklenemedi:', error);
      setRows([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
      openNotification('error', 'Hata', 'Kontrol raporları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [id, queryParams]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const handleFilter = (values: FilterValues) => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilters(values);
  };

  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilters({});
  };

  const handleDownloadReport = async (record: CompanyControlReportGroup) => {
    const proposalId = Number(record.proposalId);
    const taskId = Number(record.taskId);
    const attributeId = Number(record.attributeId);
    const orderNo = record.isBulkReport ? 1 : Number(record.orderNo);
    const bulkGroupId = record.isBulkReport ? Number(record.bulkGroupId) : undefined;

    if (!id || !proposalId || !taskId || !attributeId || !orderNo || (record.isBulkReport && !bulkGroupId)) {
      openNotification('warning', 'Uyarı', 'Raporu indirmek için gerekli bilgiler eksik.');
      return;
    }

    const key = record.groupKey || `${proposalId}-${taskId}-${attributeId}-${orderNo}`;
    setDownloadKey(key);
    try {
      const reportLabel = sanitizeFilenamePart(buildReportLabel(record));
      if (record.isBulkReport && bulkGroupId) {
        const published = await publishBulkControlTaskReport(
          proposalId,
          taskId,
          attributeId,
          orderNo,
          bulkGroupId,
        );
        const reportUrl = published.url || published.reportUrl;
        if (!reportUrl) {
          throw new Error('Yayınlanmış toplu rapor bağlantısı bulunamadı.');
        }
        saveAs(reportUrl, published.filename || `firma-kontrol-raporu-${id}-${reportLabel}.pdf`);
      } else {
        const result = await downloadControlTaskReport(proposalId, taskId, attributeId, orderNo);
        saveAs(result.blob, `firma-kontrol-raporu-${id}-${reportLabel}.pdf`);
        if (result.url) {
          URL.revokeObjectURL(result.url);
        }
      }
    } catch (error) {
      console.error('Firma kontrol raporu indirilemedi:', error);
      openNotification('error', 'Hata', 'Kontrol raporu indirilemedi.');
    } finally {
      setDownloadKey(null);
    }
  };

  const handleOpenForm = (record: CompanyControlReportGroup) => {
    const proposalId = Number(record.proposalId);
    const taskId = Number(record.taskId);
    const attributeId = Number(record.attributeId);
    const orderNo = record.isBulkReport ? 1 : Number(record.orderNo);
    const bulkGroupId = record.isBulkReport ? Number(record.bulkGroupId) : undefined;
    const bulkReportCount = record.isBulkReport
      ? Math.max(Math.floor(Number(record.bulkReportCount ?? record.savedResponseCount) || 1), 1)
      : undefined;

    if (!proposalId || !taskId || !attributeId || !orderNo || (record.isBulkReport && !bulkGroupId)) {
      openNotification('warning', 'Uyarı', 'Forma gitmek için gerekli bilgiler eksik.');
      return;
    }

    const basePath = `/control/${proposalId}/taskId/${taskId}/attribute/${attributeId}/order/${orderNo}`;
    if (record.isBulkReport) {
      window.open(`${basePath}?bulkGroupId=${bulkGroupId}&bulkReportCount=${bulkReportCount}`, '_blank', 'noopener,noreferrer');
      return;
    }
    window.open(basePath, '_blank', 'noopener,noreferrer');
  };

  const columns = [
    {
      title: 'Teklif',
      key: 'proposal',
      width: 220,
      render: (_: unknown, record: CompanyControlReportGroup) => (
        <Space direction='vertical' size={0}>
          <Text strong>{record.proposalNumber || '-'}</Text>
          <Text type='secondary'>{formatProposalDate(record.proposalCreatedAt)}</Text>
        </Space>
      ),
    },
    {
      title: 'Parametre',
      key: 'attributeName',
      render: (_: unknown, record: CompanyControlReportGroup) => (
        <Space direction='vertical' size={0}>
          <Text strong>{buildParameterTitle(record)}</Text>
          {renderDistinctiveAnswers(record)}
          {record.responseId ? <Text type='secondary'>{`Yanıt #${record.responseId}`}</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Rapor',
      key: 'report',
      width: 180,
      render: (_: unknown, record: CompanyControlReportGroup) => buildReportLabel(record),
    },
    {
      title: 'P.K.M Uzmanı',
      dataIndex: 'inspectorName',
      key: 'inspectorName',
      width: 160,
      render: (value: string) => value || '-',
    },
    {
      title: 'Tarih',
      dataIndex: 'responseUpdatedAt',
      key: 'responseUpdatedAt',
      width: 130,
      render: (value: string) => (value ? formatDate(value) : '-'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 220,
      render: (_: unknown, record: CompanyControlReportGroup) => {
        const key = record.groupKey || `${record.responseId ?? ''}`;
        return (
          <Space size={8}>
            <Tooltip title='Raporu İndir'>
              <Button
                shape='circle'
                aria-label='Raporu İndir'
                icon={<DownloadOutlined />}
                className='icon-button'
                onClick={() => handleDownloadReport(record)}
                loading={downloadKey === key}
              />
            </Tooltip>
            <Tooltip title='Kontrol Formu'>
              <Button
                shape='circle'
                aria-label='Kontrol Formu'
                icon={<FormOutlined />}
                className='icon-button'
                onClick={() => handleOpenForm(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Card className='br-30px kontrolx-card'>
      <Row align='middle' justify='space-between' gutter={[12, 12]} style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Firma Kontrol Raporları</Typography.Title>
        <Button onClick={() => navigate(`/companies/${id}`)}>Firma Detayına Dön</Button>
      </Row>
      <Form form={form} layout='inline' onFinish={handleFilter} style={{ marginBottom: 16, rowGap: 12 }}>
        <Form.Item name='statusIds'>
          <Select
            allowClear
            mode='multiple'
            showSearch
            maxTagCount='responsive'
            placeholder='Statü'
            style={{ width: 260 }}
            optionFilterProp='label'
            filterOption={(input, option) =>
              normalizeTurkish(String(option?.label ?? '')).includes(normalizeTurkish(input ?? ''))
            }
            options={PROPOSAL_STATUS_OPTIONS}
          />
        </Form.Item>
        <Form.Item name='proposalIds'>
          <Select
            allowClear
            mode='multiple'
            showSearch
            maxTagCount='responsive'
            placeholder='Teklif'
            style={{ width: 320 }}
            optionFilterProp='searchLabel'
            filterOption={(input, option) =>
              normalizeTurkish(String((option as any)?.searchLabel ?? '')).includes(normalizeTurkish(input ?? ''))
            }
            options={proposalSelectOptions}
          />
        </Form.Item>
        <Form.Item name='attributeId'>
          <Select
            allowClear
            showSearch
            placeholder='Parametre'
            style={{ width: 240 }}
            optionFilterProp='label'
            filterOption={(input, option) =>
              normalizeTurkish(String(option?.label ?? '')).includes(normalizeTurkish(input ?? ''))
            }
            options={attributeOptions.map((attribute) => ({
              value: attribute.id,
              label: attribute.name,
            }))}
          />
        </Form.Item>
        <Form.Item name='dateRange'>
          <RangePicker format='DD.MM.YYYY' placeholder={['Başlangıç', 'Bitiş']} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type='primary' htmlType='submit'>Filtrele</Button>
            <Button onClick={handleReset}>Temizle</Button>
          </Space>
        </Form.Item>
      </Form>
      <Table
        rowKey={(record) => record.groupKey}
        columns={columns as any}
        dataSource={rows}
        loading={loading}
        locale={{ emptyText: 'Kontrol raporu bulunamadı.' }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize,
            }));
          },
        }}
      />
    </Card>
  );
};

export default CompanyControlReports;
